import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { applyDocumentDirection } from "@/lib/direction";
import { CloudflareGate } from "@/components/CloudflareGate";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import EmailVerificationSent from "./pages/EmailVerificationSent";
import VerifyEmail from "./pages/VerifyEmail";
import ChangePassword from "./pages/ChangePassword";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ActivateAccount from "./pages/ActivateAccount";
import UserDashboard from "./pages/UserDashboard";
import Assessments from "./pages/Assessments";
import StrengthDetail from "./pages/StrengthDetail";
import Teams from "./pages/Teams";
import Plans from "./pages/Plans";
import Profile from "./pages/Profile";
import UserDirectory from "./pages/UserDirectory";
import DirectoryUserProfile from "./pages/DirectoryUserProfile";
import ThemeLibrary from "./pages/ThemeLibrary";
import ThemeDetail from "./pages/ThemeDetail";
import TeamStrengthsMap from "./pages/TeamStrengthsMap";
import Admin from "./pages/Admin";
import AdminThemeLeaderboardPage from "./pages/AdminThemeLeaderboardPage";
import NotFound from "./pages/NotFound";
import Invite from "./pages/Invite" ;
import PublicProfile from "./pages/PublicProfile";
import RoleRoute from "./components/RoleRoute";

const queryClient = new QueryClient();

const App = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    applyDocumentDirection(i18n.resolvedLanguage || i18n.language);
  }, [i18n.language, i18n.resolvedLanguage]);

  return (
    <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <CloudflareGate> 
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/email-verification-sent" element={<EmailVerificationSent />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:resetToken" element={<ResetPassword />} />
            <Route path="/activate-account/:token" element={<ActivateAccount />} />
            <Route element={<RoleRoute />}>
              <Route path="/profile" element={<Profile />} />
              <Route path="/invite/:token" element={<Invite />} />
            </Route>
            <Route element={<RoleRoute allowedRoles={["user"]} redirectTo="/admin" />}>
              <Route path="/dashboard" element={<UserDashboard />} />
              <Route path="/assessments" element={<Assessments />} />
              <Route path="/assessments/:strengthId" element={<StrengthDetail />} />
              <Route path="/teams" element={<Teams />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/directory" element={<UserDirectory />} />
              <Route path="/directory/:userId/profile" element={<DirectoryUserProfile />} />
              <Route path="/strengths-explorer" element={<ThemeLibrary />} />
              <Route path="/strengths-explorer/:themeId" element={<ThemeDetail />} />
              <Route path="/team-strengths-map" element={<TeamStrengthsMap />} />
              <Route path="/team-strengths-map/:themeId" element={<TeamStrengthsMap />} />
            </Route>
            <Route element={<RoleRoute allowedRoles={["admin"]} redirectTo="/dashboard" />}>
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/users/:userId/profile" element={<DirectoryUserProfile />} />
              <Route path="/admin/leaderboard/:themeId" element={<AdminThemeLeaderboardPage />} />
            </Route>
            
            <Route path="/profile/:userId/strengths" element={<PublicProfile />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
       </CloudflareGate> 
      </BrowserRouter>
    </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
